import { httpGetAdmin } from "api/admin/auth";
import { currentAdmin, setCurrentAdmin } from "helpers/storage";
import { useQuery } from "react-query";

export interface AdminSession {
  user: {
    id: number;
    email: string;
    userName: string;
    firstName?: string;
    lastName?: string;
  };
  role: { id: number; key: string; name: string } | null;
  status: "active" | "deactivated";
  countryScope: "all" | "NG" | "GH" | "KE";
  permissions: string[];
}

const storedAdminSession = (): Partial<AdminSession> | undefined => {
  const admin = currentAdmin();
  return admin?.id ? { user: admin } : undefined;
};

export const useAdminAccess = () => {
  const query = useQuery<AdminSession>("admin", httpGetAdmin, {
    initialData: storedAdminSession() as AdminSession | undefined,
    initialDataUpdatedAt: 0,
    retry: false,
    retryOnMount: false,
    staleTime: 30_000,
    onSuccess: (session) => {
      if (session?.user?.id) {
        setCurrentAdmin(session.user);
      }
    },
  });

  const permissionKeys =
    !query.isError &&
    query.data?.status === "active" &&
    Array.isArray(query.data?.permissions)
      ? query.data.permissions
      : null;
  const permissionSet = new Set(permissionKeys ?? []);

  return {
    ...query,
    admin: query.data?.user ?? currentAdmin(),
    session: query.data,
    permissions: permissionKeys,
    accessReady: permissionKeys !== null,
    can: (permissionKey: string) => permissionSet.has(permissionKey),
    canAny: (permissionKeysToCheck: string[]) =>
      permissionKeysToCheck.some((permissionKey) =>
        permissionSet.has(permissionKey),
      ),
  };
};

export default useAdminAccess;
